import Col from "antd/es/col";
import Row from "antd/es/row";
import BacktestingCard from "features/bots/BotForm/Backtesting/BacktestingCard";
import React, { ReactNode } from "react";
import { BacktestingRunType, BotType } from "@shared/types";
import useNumCardsInRow from "utils/hooks/useNumCardsInRow";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import FlatList from "common/Lists/FlatList";
import { useBottomNavigator } from "utils/hooks/misc";
import styled, { useTheme } from "styled-components";
import Skeleton from "common/NewLook/Skeleton.tsx";
import { getNewLookTokens, NEW_LOOK_RADIUS, getPanelShadow } from "common/NewLook/tokens.ts";

interface BacktestingHistoryCardsProps {
  botType: BotType;
  data?: BacktestingRunType[];
  openDeleteModal: (backtesting: BacktestingRunType) => void;
  emptyState: ReactNode;
  isLoading?: boolean;
  isFetching?: boolean;
  onRefresh?: () => void;
}

const BacktestingHistoryCards = ({
  botType,
  data,
  openDeleteModal,
  emptyState,
  isLoading,
  isFetching,
  onRefresh,
}: BacktestingHistoryCardsProps) => {
  const theme = useTheme();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const numCardsInRow = useNumCardsInRow(undefined, [700, 1650, 2000]);
  const showEmpty = !data?.length && !isLoading;

  const renderContent = ({
    item,
    index,
  }: {
    item: BacktestingRunType;
    index: number;
  }) => {
    return (
      <BacktestingCard
        key={index}
        botType={botType}
        data={item}
        setBacktestingToDelete={openDeleteModal}
        isSeeAllPage={true}
      />
    );
  };

  if (isBottomNavigator) {
    // New look: skeleton cards while the list loads, not a spinner.
    const newLookLoading = theme.isNewLook && isLoading;
    return (
      <FlatList
        data={data || []}
        loading={newLookLoading ? false : isLoading}
        renderContent={renderContent}
        onRefresh={onRefresh}
        refreshing={isFetching}
        emptyState={
          newLookLoading ? (
            <SkeletonCards aria-busy={true}>
              {Array.from({ length: 3 }, (_, index) => (
                <SkeletonCard key={index}>
                  <Skeleton width="65%" height={12} />
                  <Skeleton width={110} height={20} />
                  <Skeleton width="100%" height={10} />
                </SkeletonCard>
              ))}
            </SkeletonCards>
          ) : (
            emptyState
          )
        }
        hasActionButton={true}
      />
    );
  }

  return (
    <LoadingSpinnerOverlay
      isLoading={isLoading || isFetching}
      spinnerMarginTop={150}
      hideContentWhileLoading={true}
      spinnerSize="large"
      backgroundColor={theme.colors.layoutBgColor}
    >
      {showEmpty ? (
        emptyState
      ) : (
        <Row gutter={[16, 16]}>
          {data?.map((item, index) => {
            return (
              <Col span={24 / numCardsInRow} key={index}>
                {renderContent({ item, index })}
              </Col>
            );
          })}
        </Row>
      )}
    </LoadingSpinnerOverlay>
  );
};

export default BacktestingHistoryCards;

const SkeletonCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const SkeletonCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  box-shadow: ${(p) => getPanelShadow(p.theme)};
`;
